import classNames from 'classnames';
import React, { CSSProperties, forwardRef, MouseEventHandler, ReactNode } from 'react';

import styles from './Button.module.css';
import { Icon } from '../Icon';

export type ButtonDesign =
  | 'Default'
  | 'Emphasized'
  | 'Ghost'
  | 'Positive'
  | 'Negative'
  | 'Attention'
  | 'Transparent'
  | 'Neutral';

export type ButtonType = 'button' | 'submit' | 'reset';

export type HTMLInteractiveElement = HTMLButtonElement | HTMLAnchorElement;

/* eslint-disable @typescript-eslint/naming-convention */
const buttonClassesByDesign: Record<ButtonDesign, string | null> = {
  Default: null,
  Emphasized: 'fd-button--emphasized',
  Ghost: 'fd-button--ghost',
  Positive: 'fd-button--positive',
  Negative: 'fd-button--negative',
  Attention: 'fd-button--attention',
  Transparent: 'fd-button--transparent',
  Neutral: styles.neutral,
};
/* eslint-enable @typescript-eslint/naming-convention */

export interface ButtonProps {
  /**
   * Aria label for buttons accessibility
   */
  'aria-label'?: string | undefined;
  /**
   * The contents inside of the button element.
   * Can be omitted to show only the `icon`.
   */
  children?: ReactNode;
  /**
   * Optional class name to add to the container element.
   * Do not alter the look of the component! Should be used only for sizing and positioning.
   */
  className?: string;
  /**
   * The design of a button
   * @default "Default"
   */
  design?: ButtonDesign;
  /**
   * Whether the button is disabled or not.
   *
   * @default false
   */
  disabled?: boolean;
  /**
   * The name of the icon to be shown in the button.
   */
  icon?: string;
  /**
   * Unique ID for the element
   */
  id?: string;
  /**
   * Click event handler for the button.
   */
  onClick?: MouseEventHandler<HTMLInteractiveElement>;
  /**
   * Mouse down event handler for the button.
   */
  onMouseDown?: MouseEventHandler<HTMLInteractiveElement>;
  /**
   * Mouse up event handler for the button.
   */
  onMouseUp?: MouseEventHandler<HTMLInteractiveElement>;
  /**
   * The slot prop must be passed to the outer element of a custom component to be displayed in the right place.
   */
  slot?: string;
  /**
   * Optional inline styles to add to the container element.
   * Do not alter the look of the component! Should be used only for sizing and positioning.
   */
  style?: CSSProperties;
  /**
   * Whether the button is currently toggled.
   */
  toggled?: boolean;
  /**
   * The type of a button component.
   * Note that type defaults to `button` (instead of `submit`) which is more convenient for most cases.
   * Only has effect if `href` is not defined.
   *
   * @default "button"
   */
  type?: ButtonType;
}
export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    'aria-label': ariaLabel,
    children,
    className,
    design = 'Default',
    disabled,
    icon,
    id,
    onClick,
    onMouseDown,
    onMouseUp,
    slot,
    style,
    toggled,
    type = 'button',
  } = props;

  return (
    <button
      aria-label={
        ariaLabel || (icon && typeof children === 'string' && children) || undefined
      }
      aria-pressed={toggled ?? undefined}
      className={classNames(
        className,
        'fd-button',
        buttonClassesByDesign[design],
        toggled && 'fd-button--toggled',
      )}
      disabled={disabled}
      id={id}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      ref={ref}
      slot={slot}
      style={style}
      type={type}
    >
      {icon && (
        <Icon
          aria-label={ariaLabel || icon}
          className="sap-icon"
          name={icon}
        />
      )}
      {children && <span className="fd-button__text">{children}</span>}
    </button>
  );
});
